/* eslint-disable @typescript-eslint/ban-ts-comment */
import { FilterModel, IFilterComponentProps, IFilterProto } from '@monorepo/controlled/src/models/filter.model';
import { FilterNumberMenu } from '@monorepo/base/src/components/filters/filter-number-menu/filter-number-menu';
import { number, numberAction, numberFuncs, rangeAction, rangeFuncs } from '@monorepo/tools/src/lib/utils/number';
import styles from './filters.module.scss';
import { observer } from 'mobx-react';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { snakeCase } from 'change-case';
import { sendGtagEvent } from '@monorepo/tools/src/lib/tools/tracking';
import { TrackingActions } from '@monorepo/tools/src/lib/consts/tracking/actions';
import { EVENTS } from '@monorepo/tools/src/lib/consts/tracking/events';
import { action } from 'mobx';

interface INumberFilterProto {
	cellKey: string;
	columnName: string;
	isPercentage?: boolean;
	isRange?: boolean;
	isInventoryCommission?: boolean;
	entity?: string;
}

interface INumberFilterComponentProps extends IFilterComponentProps {
	cellKey: string;
	columnName: string;
	isPercentage?: boolean;
	isRange?: boolean;
	isInventoryCommission?: boolean;
	entity?: string;
}

const getRangeFromString = (str: string) => {
	const numbers = str
		?.replace(/[^0-9.]+/g, ' ')
		.trim()
		.split(' ')
		.filter(Boolean)
		.map(e => parseFloat(e));
	if (!Array.isArray(numbers) || !numbers.length) {
		return null;
	}
	const min = Math.min(...numbers);
	const max = Math.max(...numbers);
	const range = { min, max };
	return range;
};

const NumbersFilter = (props: INumberFilterComponentProps) => {
	const { filter, onCancel, onApply, addFilter, editFilter, columnName, isPercentage, cellKey, entity, isRange } = props;
	const protoObj = NumberFilterPrototype({ columnName, isPercentage, cellKey, entity, isRange });

	if (typeof filter?.value === 'string') {
		return <div></div>;
	}

	const onAddNumbersFilter = (_action: numberAction, _value: number) => {
		const filterLabel = `${columnName} ${_action} ${number(_value, false, false, true)}`;
		const numbersFilter = new FilterModel({
			label: filterLabel,
			action: _action,
			value: _value,
			prototype: protoObj,
		});

		if (filter?.index && editFilter) {
			editFilter(filter.index, numbersFilter);
		} else {
			if (protoObj?.multi && addFilter) {
				addFilter(numbersFilter);
			}
		}

		onApply();
	};

	return (
		<FilterNumberMenu
			title={columnName}
			defaultValue={typeof filter?.value === 'number' ? filter?.value : undefined}
			defaultAction={filter?.action}
			onApply={(_action: numberAction, _value: number) => {
				sendGtagEvent({
					action: TrackingActions.Click,
					category: EVENTS.CLICK.INDEX_PAGE.FILTERS.APPLY_FILTER,
					label: `Number filter: ${columnName}`,
					value: `${action} - ${_value}`,
				});
				onAddNumbersFilter(_action, _value);
			}}
			onCancel={() => {
				sendGtagEvent({
					action: TrackingActions.Click,
					category: EVENTS.CLICK.INDEX_PAGE.FILTERS.CANCEL_FILTER,
					label: `Number filter: ${columnName}`,
				});
				onCancel();
			}}
			inputClassNames={styles.numberFilterInputField}
			debugProps={{ dataAttrs: [new DataAttribute('id', `${snakeCase(columnName)}_menu`)] }}
			isRange={isRange}
			// isInventoryCommission={isInventoryCommission}
		/>
	);
};

export const NumberFilterPrototype = (props: INumberFilterProto): IFilterProto => {
	const { columnName, isPercentage, cellKey, entity, isRange } = props;

	return {
		MenuComponent: columnName,
		FilterComponent: observer((props: IFilterComponentProps) =>
			NumbersFilter({ ...props, columnName, isPercentage, cellKey, entity, isRange })
		),
		prop: `${entity}${cellKey}`,
		deletable: true,
		multi: true,
		filterFunc<T>(models: Array<T>, filter: FilterModel): Array<T> {
			const { value, action } = filter;
			let _value = value as number;

			if (isPercentage) {
				_value = _value / 100;
			}

			return models.filter(model => {
				if (!action) {
					return true;
				}
				const actionFunc = isRange ? rangeFuncs[action as rangeAction] : numberFuncs[action as numberAction];
				if (!actionFunc) {
					return true;
				}

				if (isRange) {
					//@ts-ignore
					const range = getRangeFromString(model[cellKey] as string);

					//@ts-ignore
					return range !== null ? actionFunc(range, _value) : true;
				}

				//@ts-ignore
				return actionFunc(model[cellKey] || 0, _value);
			});
		},
	};
};
